import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiList,
  FiRefreshCw,
  FiFilter,
  FiX,
  FiRotateCcw,
  FiCalendar,
  FiAward,
  FiGrid,
} from "react-icons/fi";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../api/axios";
import DynamicTable from "../Common/DynamicTable";
import { taskColumns, isTaskCompleted, isTaskOverdue } from "./TasksColumn";
import TaskCalendarView from "./TaskCalendarView";
import TaskCanvasView from "./TaskCanvasView";
import LeadDrawer from "../Leads/LeadDrawer";
import DealsDrawer from "../Deals/DealsDrawer";
import FollowUpModal from "../FollowUp/FollowUpModal";
import DateFilter from "../Common/DateFilter";
import { buildDateFilterParams } from "../../utils/dateFilterUtils";
import "../../assets/css/Tasks/MyTasksList.css";

const TASK_STATUS_LABELS = {
  all_active: "All Active Tasks",
  Pending: "Pending",
  "In Progress": "In Progress",
  overdue: "Overdue",
  Completed: "Completed",
  no_due_date: "No Due Date",
};

export default function MyTasksList({ defaultView }) {
  const [tasks, setTasks] = useState([]);
  const [cards, setCards] = useState({
    total_tasks: 0,
    pending_tasks: 0,
    completed_tasks: 0,
    overdue_tasks: 0,
  });
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialView =
    defaultView ||
    searchParams.get("view") ||
    (location.pathname?.endsWith("/canvas") ? "canvas" : "table");
  const [viewMode, setViewMode] = useState(initialView);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealDrawerOpen, setDealDrawerOpen] = useState(false);
  const [followUpModalState, setFollowUpModalState] = useState({
    isOpen: false,
    type: "lead",
    id: null,
  });
  const [drawerTab, setDrawerTab] = useState("overview");
  const [sortConfig, setSortConfig] = useState({ key: "due_date", direction: "asc" });
  const [dateFilter, setDateFilter] = useState(null);
  const requestIdRef = useRef(0);

  const user = useMemo(() => JSON.parse(sessionStorage.getItem("user") || "{}"), []);
  const normalizedRole = (user?.role || "").toLowerCase().replace(/\s+/g, "_");
  const isSuperAdmin = normalizedRole === "super_admin" || normalizedRole === "admin";
  const isManager = normalizedRole === "sales_manager" || normalizedRole === "manager";
  const canFilterByUser = isSuperAdmin || isManager;
  const isExecutive = normalizedRole === "sales_executive";

  const [selectedUserId, setSelectedUserId] = useState("");
  const [filterableUsers, setFilterableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Fetch filterable users for Super Admins and Managers
  useEffect(() => {
    if (!canFilterByUser) {
      setFilterableUsers([]);
      return;
    }

    let isMounted = true;
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await api.get("/tasks/users");
        if (isMounted) {
          setFilterableUsers(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        console.error("Failed to fetch task filterable users:", err);
        if (isMounted) {
          setFilterableUsers([]);
        }
      } finally {
        if (isMounted) {
          setLoadingUsers(false);
        }
      }
    };

    fetchUsers();

    return () => {
      isMounted = false;
    };
  }, [canFilterByUser]);

  const selectedUserName = useMemo(() => {
    if (!selectedUserId) return null;
    const found = filterableUsers.find((u) => String(u.id) === String(selectedUserId));
    return found ? (found.name || found.email || `User #${found.id}`) : `User #${selectedUserId}`;
  }, [selectedUserId, filterableUsers]);

  const fetchData = useCallback(
    async (filter = dateFilter, userFilter = selectedUserId) => {
      const currentRequestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const taskEndpoint = isExecutive ? "/my-tasks" : "/tasks";
        const cardsEndpoint = isExecutive ? "/tasks/cards?mine=true" : "/tasks/cards";
        const params = buildDateFilterParams(filter);
        if (userFilter) {
          params.assigned_to = userFilter;
        }
        const hasParams = Object.keys(params).length > 0;

        const [tasksRes, cardsRes] = await Promise.all([
          (async () => {
            try {
              return await (hasParams ? api.get(taskEndpoint, { params }) : api.get(taskEndpoint));
            } catch (err) {
              console.error("Failed to fetch tasks:", err);
              return { data: [] };
            }
          })(),
          (async () => {
            try {
              return await (hasParams ? api.get(cardsEndpoint, { params }) : api.get(cardsEndpoint));
            } catch (err) {
              console.error("Failed to fetch task cards:", err);
              return {
                data: {
                  total_tasks: 0,
                  pending_tasks: 0,
                  completed_tasks: 0,
                  overdue_tasks: 0,
                },
              };
            }
          })(),
        ]);

        if (currentRequestId !== requestIdRef.current) return;

        setTasks(Array.isArray(tasksRes?.data) ? tasksRes.data : []);
        setCards(
          cardsRes?.data || {
            total_tasks: 0,
            pending_tasks: 0,
            completed_tasks: 0,
            overdue_tasks: 0,
          }
        );
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("Failed to fetch tasks data:", err);
        setTasks([]);
        setCards({
          total_tasks: 0,
          pending_tasks: 0,
          completed_tasks: 0,
          overdue_tasks: 0,
        });
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [isExecutive, dateFilter, selectedUserId]
  );

  const fetchTasks = useCallback(
    (filter = dateFilter, userFilter = selectedUserId) => fetchData(filter, userFilter),
    [fetchData, dateFilter, selectedUserId]
  );
  const fetchCards = useCallback(
    (filter = dateFilter, userFilter = selectedUserId) => fetchData(filter, userFilter),
    [fetchData, dateFilter, selectedUserId]
  );

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    fetchData(newFilter, selectedUserId);
  };

  const handleUserFilterChange = (newUserId) => {
    setSelectedUserId(newUserId);
    fetchData(dateFilter, newUserId);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ==========================================================
  // COIN & POINT SYSTEM: Total User Points Calculation
  // - Accumulates points for the logged-in user's completed tasks
  // - On-time completion: +5 points
  // - Overdue completion: -5 points penalty
  // - Only completed tasks generate realized points in the user's score ledger
  // ==========================================================
  const totalUserPoints = useMemo(() => {
    return tasks.reduce((sum, t) => {
      if (t.status === "Completed") {
        return sum + (t.points !== undefined && t.points !== null ? t.points : 5);
      }
      return sum;
    }, 0);
  }, [tasks]);

  // Row selection for bulk actions
  const handleSelect = (id) => {
    const sId = String(id);
    setSelectedIds((prev) =>
      prev.includes(sId) ? prev.filter((x) => x !== sId) : [...prev, sId]
    );
  };

  const handleSelectAll = () => {
    const allIds = sortedTasks.map((t) => String(t.id));
    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  // When user clicks complete on a task:
  // If linked to Lead or Deal, open Follow-up modal FIRST!
  // The task status will ONLY be updated to Completed once the user actually completes and saves the follow-up!
  const handleInitiateComplete = async (task) => {
    // 1. Prioritize Deal follow-up if task is linked to a deal
    const dealId = task.deal_id || task.deal?.id;
    if (dealId) {
      setFollowUpModalState({
        isOpen: true,
        type: "deal",
        id: dealId,
        task: task,
      });
      return;
    }

    // 2. Fallback to Lead follow-up if task is linked to a lead
    const leadId = task.lead_id || task.lead?.id;
    if (leadId) {
      setFollowUpModalState({
        isOpen: true,
        type: "lead",
        id: leadId,
        task: task,
      });
      return;
    }

    // Standalone task without lead or deal
    const confirmResult = await Swal.fire({
      title: "Complete Task?",
      text: `Mark "${task.title}" as completed?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, complete",
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#64748b",
    });

    if (!confirmResult.isConfirmed) return;

    try {
      const res = await api.patch(`/tasks/${task.id}/complete`);
      const updatedTask = res.data;
      const isOverdue = task.due_date && new Date(task.due_date) < new Date();
      const pts = updatedTask?.points ?? (isOverdue ? -5 : 5);

      Swal.fire({
        icon: pts >= 0 ? "success" : "warning",
        title: pts >= 0 ? "Task Completed (+5 Points)" : "Task Completed Overdue (-5 Points)",
        text: `"${task.title}" has been marked as completed.`,
        timer: 1600,
        showConfirmButton: false,
      });

      fetchTasks();
      fetchCards();
    } catch (err) {
      console.error("Failed to complete task:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Could not mark task as completed.",
      });
    }
  };

  // Called ONLY when follow-up is successfully completed and submitted
  const handleFollowUpSuccess = async () => {
    const completingTask = followUpModalState.task;
    setFollowUpModalState({ isOpen: false, type: "lead", id: null, task: null });

    if (completingTask) {
      try {
        const res = await api.patch(`/tasks/${completingTask.id}/complete`);
        const updatedTask = res.data;
        const isOverdue =
          completingTask.due_date && new Date(completingTask.due_date) < new Date();
        const pts = updatedTask?.points ?? (isOverdue ? -5 : 5);

        Swal.fire({
          icon: pts >= 0 ? "success" : "warning",
          title:
            pts >= 0
              ? "Follow-up & Task Completed (+5 Points)"
              : "Follow-up & Task Completed Overdue (-5 Points)",
          text: `"${completingTask.title}" and follow-up have been successfully updated.`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error("Failed to mark task complete after follow-up:", err);
      }
    }

    fetchTasks();
    fetchCards();
  };

  const handleFollowUpClose = () => {
    // If user closes or cancels the follow-up modal, DO NOT complete the task
    setFollowUpModalState({ isOpen: false, type: "lead", id: null, task: null });
  };

  // Reopen task (Mark as Incomplete)
  const handleReopenTask = async (task) => {
    const confirmResult = await Swal.fire({
      title: "Reopen Task?",
      text: `Mark "${task.title}" as incomplete?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, reopen",
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#64748b",
    });

    if (!confirmResult.isConfirmed) return;

    try {
      await api.patch(`/tasks/${task.id}/status`, { status: "Pending" });
      Swal.fire({
        icon: "success",
        title: "Task Reopened",
        text: `"${task.title}" has been marked as incomplete.`,
        timer: 1500,
        showConfirmButton: false,
      });
      fetchTasks();
      fetchCards();
    } catch (err) {
      console.error("Failed to reopen task:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Could not reopen task.",
      });
    }
  };

  // Delete single task
  const handleDeleteSingleTask = async (task) => {
    if (!canDelete) {
      Swal.fire({
        icon: "error",
        title: "Permission Denied",
        text: "Only Super Admins can delete tasks.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete "${task.title}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/tasks/${task.id}`);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: `"${task.title}" deleted successfully.`,
        timer: 1500,
        showConfirmButton: false,
      });
      fetchTasks();
      fetchCards();
    } catch (err) {
      console.error("Failed to delete task:", err);
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: err.response?.data?.detail || "Could not delete task.",
      });
    }
  };

  // Open related deal or lead drawer
  const openTask = async (taskOrId) => {
    let target = taskOrId;
    if (typeof taskOrId === "number" || typeof taskOrId === "string") {
      target = tasks.find((t) => String(t.id) === String(taskOrId));
      if (!target) {
        try {
          const res = await api.get(`/tasks/${taskOrId}`);
          target = res.data;
        } catch (err) {
          console.error("Failed to load task:", err);
        }
      }
    }
    if (!target) return;

    // 1. Prioritize Deal context if task is linked to a deal
    if (target.deal_id) {
      try {
        const res = await api.get(`/deal/${target.deal_id}`);
        setSelectedDeal(res.data);
        setDrawerTab(location.state?.openFollowUp ? "timeline" : "overview");
        setDealDrawerOpen(true);
        return;
      } catch (err) {
        console.error("Failed to load deal details:", err);
        Swal.fire({
          icon: "error",
          title: "Failed to load deal",
          text: "Unable to load the related deal details.",
        });
        return;
      }
    }

    // 2. Fallback to Lead context if task is linked to a lead
    const leadId = target.lead_id || target.lead?.id;
    if (leadId) {
      try {
        const res = await api.get(`/lead/get/${leadId}`);
        setSelectedLead(res.data);
        setDrawerTab(location.state?.openFollowUp ? "activity" : "overview");
        setLeadDrawerOpen(true);
        return;
      } catch (err) {
        console.error("Failed to load lead details:", err);
        Swal.fire({
          icon: "error",
          title: "Failed to load lead",
          text: "Unable to load the related lead details.",
        });
        return;
      }
    }

    Swal.fire({
      icon: "info",
      title: "Task Details",
      text: `${target.title}: ${target.description || "No description provided."}`,
    });
  };

  useEffect(() => {
    if (location.state?.openTaskId) {
      setDrawerTab(location.state.openFollowUp ? "activity" : "overview");
      openTask(location.state.openTaskId);
    }
  }, [location.state]);

  // Bulk Delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete ${selectedIds.length} selected task(s)? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      await api.post("/tasks/bulk-delete", {
        task_ids: selectedIds.map(Number),
      });

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: `${selectedIds.length} task(s) deleted successfully.`,
        timer: 1500,
        showConfirmButton: false,
      });

      setSelectedIds([]);
      fetchTasks();
      fetchCards();
    } catch (err) {
      console.error("Failed to delete tasks:", err);
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: err.response?.data?.detail || "Could not delete selected tasks.",
      });
    }
  };

  const handleCardFilter = (status) => {
    setStatusFilter((prev) => (prev === status ? "" : status));
  };

  // Filter Tasks
  const filteredTasks = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const safeSearch = (search || "").trim().toLowerCase();

    return tasks.filter((task) => {
      const searchTarget = `
        ${task.title || ""}
        ${task.description || ""}
        ${task.assigned_user_name || ""}
        ${task.organization_name || ""}
        ${task.lead_title || ""}
        ${task.deal_title || ""}
        ${task.contact_name || ""}
      `.toLowerCase();

      const matchesSearch = !safeSearch || searchTarget.includes(safeSearch);

      // Status & Task Filter
      let matchesStatus = true;
      const isCompleted = isTaskCompleted(task);
      const isOverdue = isTaskOverdue(task);

      if (!statusFilter || statusFilter === "all_active") {
        // Exclude tasks with status Completed from default task list
        matchesStatus = !isCompleted;
      } else {
        const sFilter = statusFilter.toLowerCase();
        if (sFilter === "completed" || sFilter === "complete") {
          matchesStatus = isCompleted;
        } else if (sFilter === "overdue") {
          matchesStatus = isOverdue;
        } else if (sFilter === "pending") {
          matchesStatus = !isCompleted && task.status?.toLowerCase() === "pending";
        } else if (sFilter === "in progress" || sFilter === "in_progress") {
          matchesStatus = !isCompleted && (task.status === "In Progress" || task.status === "in_progress");
        } else if (sFilter === "no_due_date") {
          matchesStatus = !isCompleted && !task.due_date;
        } else if (sFilter === "today") {
          if (!task.due_date || isCompleted) {
            matchesStatus = false;
          } else {
            const taskDueDate = new Date(task.due_date);
            matchesStatus = taskDueDate >= startOfToday && taskDueDate <= endOfToday;
          }
        } else if (sFilter === "tomorrow") {
          if (!task.due_date || isCompleted) {
            matchesStatus = false;
          } else {
            const startOfTomorrow = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);
            const endOfTomorrow = new Date(endOfToday.getTime() + 24 * 60 * 60 * 1000);
            const taskDueDate = new Date(task.due_date);
            matchesStatus = taskDueDate >= startOfTomorrow && taskDueDate <= endOfTomorrow;
          }
        } else if (sFilter === "this_week") {
          if (!task.due_date || isCompleted) {
            matchesStatus = false;
          } else {
            const dayOfWeek = now.getDay();
            const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
            const endOfWeek = new Date(
              now.getFullYear(),
              now.getMonth(),
              now.getDate() + daysUntilSunday,
              23,
              59,
              59,
              999
            );
            const taskDueDate = new Date(task.due_date);
            matchesStatus = taskDueDate >= startOfToday && taskDueDate <= endOfWeek;
          }
        } else {
          matchesStatus = task.status?.toLowerCase() === sFilter;
        }
      }

      // User filter
      const matchesUser =
        !selectedUserId || String(task.assigned_to) === String(selectedUserId);

      return matchesSearch && matchesStatus && matchesUser;
    });
  }, [tasks, search, statusFilter, selectedUserId]);

  const hasActiveFilters = Boolean(
    search ||
      (statusFilter && statusFilter !== "all_active") ||
      selectedUserId
  );

  const resetAllFilters = () => {
    setSearch("");
    setStatusFilter("");
    setSelectedUserId("");
    fetchData(dateFilter, "");
  };

  // Sorting
  const sortedTasks = useMemo(() => {
    if (!sortConfig.key) return filteredTasks;

    return [...filteredTasks].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "due_date") {
        aVal = aVal ? new Date(aVal).getTime() : Infinity;
        bVal = bVal ? new Date(bVal).getTime() : Infinity;
      }

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredTasks, sortConfig]);

  const handleSortChange = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const canDelete = normalizedRole === "super_admin";

  const userFilteredTasks = useMemo(() => {
    if (!selectedUserId) return tasks;
    return tasks.filter((t) => String(t.assigned_to) === String(selectedUserId));
  }, [tasks, selectedUserId]);

  const pageTitle =
    normalizedRole === "super_admin"
      ? "All Tasks"
      : normalizedRole === "sales_manager"
      ? "Team Tasks"
      : "My Tasks";

  const pageSubtitle =
    normalizedRole === "super_admin"
      ? "Overview and management of all organizational tasks across the CRM."
      : normalizedRole === "sales_manager"
      ? "Monitor and manage follow-up tasks across your sales executive team."
      : "Manage, track, and complete your assigned follow-up tasks.";

  return (
    <div className="tasks-wrapper">
      {/* Page Header */}
      <div className="tasks-header">
            <div className="tasks-header-left">
              <h2>{pageTitle}</h2>
              <p>{pageSubtitle}</p>
            </div>

            <div className="tasks-header-actions">
              {/* View Switcher */}
              <div className="view-mode-toggle">
                <button
                  type="button"
                  className={`btn-view-toggle ${viewMode === "table" ? "active" : ""}`}
                  onClick={() => setViewMode("table")}
                  title="Table View"
                >
                  <FiList /> Table View
                </button>
                <button
                  type="button"
                  className={`btn-view-toggle ${viewMode === "calendar" ? "active" : ""}`}
                  onClick={() => setViewMode("calendar")}
                  title="Calendar View"
                >
                  <FiCalendar /> Calendar View
                </button>
                <button
                  type="button"
                  className={`btn-view-toggle ${viewMode === "canvas" ? "active" : ""}`}
                  onClick={() => setViewMode("canvas")}
                  title="Canvas View"
                >
                  <FiGrid /> Canvas View
                </button>
              </div>

              <DateFilter value={dateFilter} onChange={handleDateFilterChange} />
              {canFilterByUser && filterableUsers.length > 0 && (
                <div className="task-header-user-filter">
                  <select
                    id="taskHeaderUserFilter"
                    data-testid="task-user-filter"
                    aria-label="Filter by User"
                    className="task-user-filter-select"
                    value={selectedUserId}
                    onChange={(e) => handleUserFilterChange(e.target.value)}
                  >
                    <option value="">Filter by User</option>
                    {filterableUsers.map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {u.name || u.email || `User #${u.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                className="btn-refresh-task"
                onClick={() => fetchTasks(dateFilter)}
                title="Refresh Tasks"
                aria-label="Refresh Tasks"
              >
                <FiRefreshCw className={loading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          {/* KPI Stat Cards (No In Progress, Added Points Total Card) */}
          <div className={`tasks-stats-grid${loading ? " refreshing" : ""}`}>
            <div
              className="task-stat-card"
              onClick={() => setStatusFilter("")}
              title="Click to view all tasks"
            >
              <div className="task-stat-icon total">
                <FiList />
              </div>
              <div className="task-stat-info">
                <p>Total Tasks</p>
                <h3>{cards?.total_tasks ?? 0}</h3>
              </div>
            </div>

            <div
              className="task-stat-card"
              onClick={() => handleCardFilter("Pending")}
              title="Click to filter by Pending"
            >
              <div className="task-stat-icon pending">
                <FiClock />
              </div>
              <div className="task-stat-info">
                <p>Pending</p>
                <h3>{cards?.pending_tasks ?? 0}</h3>
              </div>
            </div>

            <div
              className="task-stat-card"
              onClick={() => handleCardFilter("Completed")}
              title="Click to filter by Completed"
            >
              <div className="task-stat-icon completed">
                <FiCheckCircle />
              </div>
              <div className="task-stat-info">
                <p>Completed</p>
                <h3>{cards?.completed_tasks ?? 0}</h3>
              </div>
            </div>

            <div
              className="task-stat-card"
              onClick={() => handleCardFilter("overdue")}
              title="Click to filter by Overdue"
            >
              <div className="task-stat-icon overdue">
                <FiAlertCircle />
              </div>
              <div className="task-stat-info">
                <p>Overdue</p>
                <h3 style={{ color: (cards?.overdue_tasks ?? 0) > 0 ? "#dc2626" : undefined }}>
                  {cards?.overdue_tasks ?? 0}
                </h3>
              </div>
            </div>

            <div
              className="task-stat-card points-card"
              title="User Points (+5 on time, -5 overdue)"
            >
              <div className="task-stat-icon points" style={{ background: "#fef3c7", color: "#d97706" }}>
                <FiAward />
              </div>
              <div className="task-stat-info">
                <p>My Points</p>
                <h3 style={{ color: totalUserPoints >= 0 ? "#16a34a" : "#dc2626" }}>
                  {totalUserPoints >= 0 ? `${totalUserPoints}` : totalUserPoints}
                </h3>
              </div>
            </div>
          </div>

        {/* Main Content Area: Canvas View, Calendar View, or Table Card */}
        {viewMode === "canvas" ? (
          <TaskCanvasView
            tasks={userFilteredTasks}
            loading={loading}
            onTaskClick={(taskId) => {
              setDrawerTab("overview");
              openTask(taskId);
            }}
            onCompleteTask={handleInitiateComplete}
            onReopenTask={handleReopenTask}
            onDeleteTask={handleDeleteSingleTask}
            canDelete={canDelete}
          />
        ) : viewMode === "calendar" ? (
          <TaskCalendarView
            tasks={sortedTasks}
            onTaskClick={(taskId) => {
              setDrawerTab("overview");
              openTask(taskId);
            }}
            onCompleteTask={handleInitiateComplete}
            loading={loading}
          />
        ) : (
          <div className="table-card">
              {/* Active Filters Bar */}
              {hasActiveFilters && (
                <div className="task-active-filters-bar">
                  <div className="task-active-filters-left">
                    <span className="task-active-filters-title">
                      <FiFilter /> Active Filters:
                    </span>
                    <div className="task-active-chips">
                      {search && (
                        <span className="task-filter-chip">
                          Search: "{search}"
                          <button
                            type="button"
                            onClick={() => setSearch("")}
                            aria-label="Remove search filter"
                          >
                            <FiX />
                          </button>
                        </span>
                      )}
                      {statusFilter && statusFilter !== "all_active" && (
                        <span className="task-filter-chip">
                          Status: {TASK_STATUS_LABELS[statusFilter] || statusFilter}
                          <button
                            type="button"
                            onClick={() => setStatusFilter("")}
                            aria-label="Remove status filter"
                          >
                            <FiX />
                          </button>
                        </span>
                      )}
                      {selectedUserId && (
                        <span className="task-filter-chip">
                          User: {selectedUserName}
                          <button
                            type="button"
                            onClick={() => handleUserFilterChange("")}
                            aria-label="Remove user filter"
                          >
                            <FiX />
                          </button>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="task-active-filters-right">
                    <span className="task-results-counter">
                      Showing <strong>{sortedTasks.length}</strong> of{" "}
                      <strong>{tasks.length}</strong> tasks
                    </span>
                    <button
                      type="button"
                      className="btn-reset-filters"
                      onClick={resetAllFilters}
                      title="Reset all filters"
                      aria-label="Reset all filters"
                    >
                      <FiRotateCcw /> Reset Filters
                    </button>
                  </div>
                </div>
              )}

              <DynamicTable
                data={sortedTasks}
                columns={taskColumns({
                  selectedIds,
                  handleSelect,
                  handleSelectAll,
                  totalRows: sortedTasks.length,
                  onCompleteTask: handleInitiateComplete,
                })}
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search by task title, description, entity..."
                onRowClick={(taskId) => {
                  setDrawerTab("overview");
                  openTask(taskId);
                }}
                selectedIds={selectedIds}
                selectedCount={selectedIds.length}
                onBulkDelete={handleBulkDelete}
                sortConfig={sortConfig}
                onSortChange={handleSortChange}
                canDelete={canDelete}
                storageKey="my_tasks_visible_columns_v4"
                filters={[
                  {
                    name: "status",
                    id: "taskStatusFilter",
                    testId: "task-status-filter",
                    ariaLabel: "Task Status",
                    value: statusFilter,
                    onChange: setStatusFilter,
                    options: [
                      { label: "All Active Tasks", value: "all_active" },
                      { label: "Pending", value: "Pending" },
                      { label: "In Progress", value: "In Progress" },
                      { label: "Overdue", value: "overdue" },
                      { label: "Completed", value: "Completed" },
                      { label: "No Due Date", value: "no_due_date" },
                    ],
                    placeholder: "Task Status",
                  },
                ]}
              />
            </div>
          )}

      {/* Follow-Up Modal (Triggered upon task completion or directly) */}
      {followUpModalState.isOpen && (
        <FollowUpModal
          type={followUpModalState.type}
          id={followUpModalState.id}
          isOpen={followUpModalState.isOpen}
          onClose={handleFollowUpClose}
          onSuccess={handleFollowUpSuccess}
        />
      )}

      {/* Related Lead Drawer */}
      <LeadDrawer
        lead={selectedLead}
        isOpen={leadDrawerOpen}
        onClose={() => {
          setLeadDrawerOpen(false);
          setSelectedLead(null);
          setDrawerTab("overview");
        }}
        refreshLeads={fetchTasks}
        initialTab={drawerTab}
      />

      {/* Related Deal Drawer */}
      <DealsDrawer
        dealId={selectedDeal?.id}
        deal={selectedDeal}
        isOpen={dealDrawerOpen}
        onClose={() => {
          setDealDrawerOpen(false);
          setSelectedDeal(null);
          setDrawerTab("overview");
        }}
        refreshDeals={fetchTasks}
        initialTab={drawerTab}
      />
    </div>
  );
}
